import { getEnv } from "@trillion/ui/lib/env";
import { createApiClient } from "@trillion/ui/lib/api-client";
import type {
  User,
  PaginatedResponse,
  UserListParams,
  UserUpdateData,
  UserStats,
  InviteUserRequest,
  InviteUserResult,
  ResendResult,
  OAuthClient,
  CreateOAuthClientRequest,
  UpdateOAuthClientRequest,
  Product,
} from "@/types";

const api = createApiClient(() => getEnv("PASSPORT_API_URL"));

export const adminApi = {
  users: {
    async list(params: UserListParams = {}): Promise<PaginatedResponse<User>> {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set("page", String(params.page));
      if (params.pageSize) searchParams.set("pageSize", String(params.pageSize));
      if (params.search) searchParams.set("search", params.search);
      if (params.userStatusId !== undefined)
        searchParams.set("userStatusId", String(params.userStatusId));
      if (params.sortBy) searchParams.set("sortBy", params.sortBy);
      if (params.sortDesc !== undefined)
        searchParams.set("sortDesc", String(params.sortDesc));

      const query = searchParams.toString();
      return api.request<PaginatedResponse<User>>(
        `/admin/users${query ? `?${query}` : ""}`
      );
    },

    async get(userId: string): Promise<User> {
      return api.request<User>(`/admin/users/${userId}`);
    },

    async update(userId: string, data: UserUpdateData): Promise<void> {
      return api.request<void>(`/admin/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },

    async addRole(userId: string, product: string, role: string): Promise<void> {
      return api.request<void>(`/api/users/${userId}/roles`, {
        method: "PUT",
        body: JSON.stringify({ product, role }),
      });
    },

    async removeRole(userId: string, product: string, role: string): Promise<void> {
      return api.request<void>(`/api/users/${userId}/roles`, {
        method: "DELETE",
        body: JSON.stringify({ product, role }),
      });
    },

    async stats(): Promise<UserStats> {
      return api.request<UserStats>("/admin/users/stats");
    },

    async invite(data: InviteUserRequest): Promise<InviteUserResult> {
      return api.request<InviteUserResult>("/admin/users/invite", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    // Re-issue the pending invitation's token and re-send the invite email. No request body. Throws with
    // the server body on 404 / 409 not_invited / 409 no_pending_invitation / 502 email_send_failed.
    async resendInvite(userId: string): Promise<ResendResult> {
      return api.request<ResendResult>(`/admin/users/${userId}/resend-invite`, { method: "POST" });
    },

    // Send a verification email to an active, unverified user. No request body. Throws with the server
    // body on 404 / 409 not_active / 409 already_verified / 502 email_send_failed.
    async resendVerification(userId: string): Promise<ResendResult> {
      return api.request<ResendResult>(`/admin/users/${userId}/resend-verification`, { method: "POST" });
    },
  },

  // OAuth clients (Applications). GET list returns a plain array (unpaginated). No delete in v1.
  oauthClients: {
    async list(): Promise<OAuthClient[]> {
      return api.request<OAuthClient[]>("/admin/oauth-clients");
    },

    async get(clientId: string): Promise<OAuthClient> {
      return api.request<OAuthClient>(
        `/admin/oauth-clients/${encodeURIComponent(clientId)}`
      );
    },

    async create(data: CreateOAuthClientRequest): Promise<OAuthClient> {
      return api.request<OAuthClient>("/admin/oauth-clients", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    async update(
      clientId: string,
      data: UpdateOAuthClientRequest
    ): Promise<OAuthClient> {
      return api.request<OAuthClient>(
        `/admin/oauth-clients/${encodeURIComponent(clientId)}`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        }
      );
    },
  },

  // Role-assignable products for the role/invite dropdowns (GET /admin/products — plain array, admin-gated).
  products: {
    async list(): Promise<Product[]> {
      return api.request<Product[]>("/admin/products");
    },
  },
};
